import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  SelectChangeEvent,
  Box,
  Chip,
  Typography,
  IconButton,
  Divider
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Send as SendIcon,
  Close as CloseIcon,
  AdminPanelSettings as AdminIcon,
  EditNote as EditorIcon
} from '@mui/icons-material';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useUser } from '../../contexts/UserContext';
import { db } from '../../firebase/config';

interface AdminInviteDialogProps {
  open: boolean;
  onClose: (refreshData?: boolean) => void;
}

const AdminInviteDialog: React.FC<AdminInviteDialogProps> = ({ open, onClose }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('editor');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { currentUser } = useUser();
  
  // Reset form
  const resetForm = () => {
    setEmail('');
    setRole('editor');
    setError(null);
    setSuccess(false);
  };

  // Handle role change
  const handleRoleChange = (event: SelectChangeEvent) => {
    setRole(event.target.value);
  };
  
  // Handle invite submission
  const handleSubmit = async () => {
    // Validate email
    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Create system invitation record
      await addDoc(collection(db, 'systemInvitations'), {
        email: email.trim().toLowerCase(),
        role: role,
        status: 'pending',
        invitedBy: currentUser?.uid,
        inviterName: currentUser?.displayName || 'System Admin',
        includeFreeTrial: true, // Flag to indicate this user gets a free trip
        createdAt: serverTimestamp(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days from now
      });
      
      // Create notification record
      await addDoc(collection(db, 'notifications'), {
        type: 'system_invitation',
        recipientEmail: email.trim().toLowerCase(),
        message: `You've been invited to the Travel Companion app with ${role} permissions by ${currentUser?.displayName || 'System Admin'}. You'll get a free trip on your account.`,
        createdAt: serverTimestamp(),
        status: 'unread'
      });
      
      setSuccess(true);
      
      // Close dialog after a short delay
      setTimeout(() => {
        onClose(true); // Refresh data
        resetForm();
      }, 1500);
      
    } catch (err) {
      console.error('Error sending invitation:', err);
      setError('Failed to send invitation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={handleCloseDialog} 
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Invite System User</Typography>
          <IconButton edge="end" aria-label="close" onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Invitation sent successfully!
          </Alert>
        )}
        
        <DialogContentText>
          Invite a user to join the system with specific permissions. They'll receive an email with instructions on how to join and will get a free trip on their account.
        </DialogContentText>
        
        <TextField
          autoFocus
          margin="dense"
          label="Email Address"
          type="email"
          fullWidth
          variant="outlined"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading || success}
        />
        
        <FormControl fullWidth margin="normal">
          <InputLabel id="invite-role-label">Permission Level</InputLabel>
          <Select
            labelId="invite-role-label"
            value={role}
            label="Permission Level"
            onChange={handleRoleChange}
            disabled={loading || success}
          >
            <MenuItem value="editor">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EditorIcon sx={{ mr: 1 }} />
                Editor
              </Box>
            </MenuItem>
            <MenuItem value="owner">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AdminIcon sx={{ mr: 1 }} />
                Owner
              </Box>
            </MenuItem>
          </Select>
        </FormControl>
        
        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Permission Details
          </Typography>
          <Divider sx={{ my: 1 }} />
          
          {role === 'owner' ? (
            <>
              <Typography variant="body2" gutterBottom>
                <strong>Owner permissions</strong> include:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, my: 1 }}>
                <Chip size="small" label="Full trip management" />
                <Chip size="small" label="Create/edit all trips" />
                <Chip size="small" label="Delete trips" />
                <Chip size="small" label="Manage all users" />
                <Chip size="small" label="Invite collaborators" />
              </Box>
            </>
          ) : (
            <>
              <Typography variant="body2" gutterBottom>
                <strong>Editor permissions</strong> include:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, my: 1 }}>
                <Chip size="small" label="Upload photos" />
                <Chip size="small" label="Add comments" />
                <Chip size="small" label="Chat with others" />
                <Chip size="small" label="View trips" />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.875rem' }}>
                Editors cannot modify trip details or manage other participants.
              </Typography>
            </>
          )}
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>New User Benefit:</strong> This user will automatically receive one free trip when they join.
            They will need to pay for additional trips.
          </Typography>
        </Alert>
        
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleCloseDialog} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary" 
          disabled={loading || success || !email}
          startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
        >
          {loading ? 'Sending...' : 'Send Invitation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminInviteDialog; 